import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Add, Delete } from '@mui/icons-material';
import { FormModal } from '../../../../3-pattern';
import { TranslatedString } from '../../../../../interfaces';
import { getBackendLanguage } from '../../../../../assets/i18n/config';
import {
  Box, Button, DroppableFileInput, Grid, IconButton, MenuItem, NumberField, SelectField, SignerTypeSelect, TextField, TranslatableTextField,
  Typography,
} from '../../../..';
import { useGlobalToast } from '../../../../../providers/globalToastProvider';
import { uploadWithRetry } from '../../../../../util/fileUploader';
import { UserContext } from '../../../../../providers/userContextProvider';

const UPDATE_FORM_DEFINITION = gql`
  mutation updateFormDefinition($input: UpdateFormDefinitionInput!) {
    updateFormDefinition(input: $input) {
      formDefinition {
        id
      }
    }
  }
`;

export const FETCH_FILE_UPLOAD_URL = gql`
  query fetchFileUploadUrl ($input: FetchFileUploadUrlInput!) {
    fetchFileUploadUrl(input:$input) {
      temporarySignedURL
      timestamp
    }
  }
`;

export const CREATE_FILE_DOCUMENT = gql`
  mutation createFileDocument ($input: CreateFileDocumentInput!) {
    createFileDocument(input:$input) {
      fileDocument {
        id name fileName s3Key type
      }
    }
  }
`;

export enum FormDefinitionSigningTypes {
  IN_APP = 'IN_APP',
  UPLOAD = 'UPLOAD',
  INTEGRATION = 'INTEGRATION',
}

export enum FormDefinitionTemplateTypes {
  HTML = 'HTML', // TODO: Not supported yet
  PDF = 'PDF',
  NONE = 'NONE',
}

export enum ObjectTypes {
  ENTITY = 'ENTITY',
  ACCOUNT = 'ACCOUNT',
  SUB_ACCOUNT = 'SUB_ACCOUNT',
  GOAL = 'GOAL',
  HOUSEHOLD = 'HOUSEHOLD',
}

export interface Webhook {
  url: string;
  apiToken?: string;
}

export interface SignerMapping {
  signerKey: string;
  pageNumber?: number;
  x?: number;
  y?: number;
}

export interface FormDefinitionInput {
  id?: string;
  key: string;
  translatedName: TranslatedString;
  translatedDescription: TranslatedString;
  signingType: FormDefinitionSigningTypes;
  templateType: FormDefinitionTemplateTypes;
  template: 'BASIC' | 'EXTERNAL';
  signerMapping: SignerMapping[];
  mappingWebhook: Webhook;
  objectType: ObjectTypes;
  organization: { id: string };
  s3Url: string;
}

export const EditFormDefinition = ({
  afterUpdate,
  formDefinitionToUpdate,
  handleClose,
  defaultOrg,
}: {
  afterUpdate: () => void;
  formDefinitionToUpdate: FormDefinitionInput;
  handleClose: () => void;
  defaultOrg: string;
}) => {
  const { t } = useTranslation(['settings']);
  const [formDefinition, setFormDefinition] = useState<FormDefinitionInput>(formDefinitionToUpdate);
  const [file, setFile] = useState<File>();
  const { showToast } = useGlobalToast();
  const { userContext } = useContext(UserContext);

  const [fetchFileUploadUrl] = useLazyQuery(FETCH_FILE_UPLOAD_URL, { fetchPolicy: 'no-cache' });
  const [createFileDocument] = useMutation(CREATE_FILE_DOCUMENT);

  const [updateFormDefinition, { loading }] = useMutation(UPDATE_FORM_DEFINITION);

  useEffect(() => {
    setFormDefinition({
      ...formDefinitionToUpdate,
      template: formDefinitionToUpdate.s3Url ? 'BASIC' : 'EXTERNAL',
    });
  }, [formDefinitionToUpdate]);

  const uploadDocument = async () => {
    if (!file) return '';

    const createFileInput: any = {
      objectType: 'ORGANIZATION',
      objectId: defaultOrg,
      fileName: file.name,
      type: 'FORM',
    };

    /* (1) fetch the S3 upload URL from backend */
    const queryResult = await fetchFileUploadUrl({ variables: { input: { ...createFileInput, userId: userContext.id, generateUniqueFileName: true } } });
    const uploadUrl = queryResult?.data?.fetchFileUploadUrl.temporarySignedURL;
    const timestamp = queryResult?.data?.fetchFileUploadUrl.timestamp;
    if (!uploadUrl || queryResult?.error) {
      showToast({ severity: 'error', message: `Upload failed, step 1 of 3 (FETCH_FILE_UPLOAD_URL) ${queryResult?.error}` });
      return '';
    }

    /* (2) do the upload */
    try {
      const uploaded: Response = await uploadWithRetry(uploadUrl, file, 'FORM');
      if (!uploaded.ok) throw (new Error(`${uploaded.status} ${uploaded.statusText}`));
    } catch (e: any) {
      showToast({ severity: 'error', message: `Upload failed, step 2 of 3 (HTTP PUT) ${e}` });
      return '';
    }

    /* (3) create the fileDocument within backend */
    createFileInput.name = file.name;
    createFileInput.mediaType = file.type;
    createFileInput.permissionType = 'ORGANIZATIONAL';
    createFileInput.sourceType = 'FormDefinition';
    createFileInput.timestamp = timestamp;

    try {
      const result = await createFileDocument({
        variables: { input: createFileInput },
      });

      return result.data.createFileDocument.fileDocument.s3Key;
    } catch (error) {
      showToast({ severity: 'error', message: `Upload failed, step 3 of 3 (CREATE_FILE_DOCUMENT) ${error}` });
      return '';
    }
  };

  const update = async () => {
    const s3Path = file ? await uploadDocument() : undefined;
    await updateFormDefinition({
      variables: {
        input: {
          formDefinitionId: formDefinition.id,
          translatedName: { en: formDefinition.translatedName.en, fr: formDefinition.translatedName.fr },
          translatedDescription: formDefinition.translatedDescription?.en ? { en: formDefinition.translatedDescription.en, fr: formDefinition.translatedDescription.fr } : undefined,
          signingType: formDefinition.signingType,
          templateType: formDefinition.templateType,
          signerMapping: formDefinition.signerMapping,
          unsignedS3Path: s3Path,
          mappingWebhook: formDefinition.mappingWebhook?.url ? {
            url: formDefinition.mappingWebhook?.url,
            apiToken: formDefinition.mappingWebhook?.apiToken,
            type: 'BEARER',
          } : undefined,
        },
      },
    });
    afterUpdate();
  };

  return (
    <FormModal
      open
      loading={loading}
      title={t('formDefinitions.formDefinitionModal.editTitle')}
      formButton={t('shared:update')}
      onSubmit={update}
      handleClose={handleClose}
      maxWidth='sm'
    >
      <Box display='flex' flexDirection='column' gap={2}>
        <TextField
          fullWidth
          label={t('formDefinitions.formDefinitionModal.key')}
          value={formDefinition.key}
          onChange={(e: any) => setFormDefinition({ ...formDefinition, key: e.target.value })}
          testId='signer-type-key'
        />
        <TranslatableTextField
          fullWidth
          label={t('formDefinitions.formDefinitionModal.name')}
          value={formDefinition.translatedName}
          onChange={(value) => setFormDefinition({ ...formDefinition, translatedName: value })}
          fallbackLanguage={getBackendLanguage()}
          testId='signer-type-name'
        />
        <TranslatableTextField
          fullWidth
          label={t('formDefinitions.formDefinitionModal.description')}
          value={formDefinition.translatedDescription}
          onChange={(value) => setFormDefinition({ ...formDefinition, translatedDescription: value })}
          fallbackLanguage={getBackendLanguage()}
          testId='signer-type-name'
        />
        <SelectField
          fullWidth
          label={t('formDefinitions.formDefinitionModal.objectType')}
          value={formDefinition.objectType}
          onChange={(e: any) => setFormDefinition({ ...formDefinition, objectType: e.target.value })}
          testId='signer-type-object-type'
        >
          <MenuItem value="ENTITY">{t('formDefinitions.formDefinitionModal.ENTITY')}</MenuItem>
          <MenuItem value="ACCOUNT">{t('formDefinitions.formDefinitionModal.ACCOUNT')}</MenuItem>
          <MenuItem value="SUB_ACCOUNT">{t('formDefinitions.formDefinitionModal.SUB_ACCOUNT')}</MenuItem>
          <MenuItem value="GOAL">{t('formDefinitions.formDefinitionModal.GOAL')}</MenuItem>
          <MenuItem value="HOUSEHOLD">{t('formDefinitions.formDefinitionModal.HOUSEHOLD')}</MenuItem>
        </SelectField>
        <SelectField
          fullWidth
          label={t('formDefinitions.formDefinitionModal.type')}
          value={`${formDefinition.templateType}|${formDefinition.signingType}`}
          onChange={(e: any) => setFormDefinition({
            ...formDefinition,
            templateType: e.target.value.split('|')[0] as FormDefinitionTemplateTypes,
            signingType: e.target.value.split('|')[1] as FormDefinitionSigningTypes,
          })}
          testId='signer-type-signing-type'
        >
          <MenuItem value={`${FormDefinitionTemplateTypes.PDF}|${FormDefinitionSigningTypes.IN_APP}`}>{t('formDefinitions.formDefinitionModal.PDF_IN_APP')}</MenuItem>
          <MenuItem value={`${FormDefinitionTemplateTypes.PDF}|${FormDefinitionSigningTypes.INTEGRATION}`}>{t('formDefinitions.formDefinitionModal.PDF_INTEGRATION')}</MenuItem>
          <MenuItem value={`${FormDefinitionTemplateTypes.NONE}|${FormDefinitionSigningTypes.UPLOAD}`}>{t('formDefinitions.formDefinitionModal.NONE_UPLOAD')}</MenuItem>
          <MenuItem value={`${FormDefinitionTemplateTypes.PDF}|${FormDefinitionSigningTypes.UPLOAD}`}>{t('formDefinitions.formDefinitionModal.PDF_UPLOAD')}</MenuItem>
        </SelectField>
        <SelectField
          fullWidth
          label={t('formDefinitions.formDefinitionModal.template')}
          value={formDefinition.template}
          onChange={(e: any) => setFormDefinition({ ...formDefinition, template: e.target.value })}
          testId='template-type'
        >
          <MenuItem value="BASIC">{t('formDefinitions.formDefinitionModal.basicTemplate')}</MenuItem>
          <MenuItem value="EXTERNAL">{t('formDefinitions.formDefinitionModal.externalTemplate')}</MenuItem>
        </SelectField>
        {formDefinition.templateType === FormDefinitionTemplateTypes.PDF && formDefinition.template === 'BASIC' && (
          <DroppableFileInput
            onFileChosen={(dropFile: File) => {
              setFile(dropFile);
            }}
          />
        )}
        {formDefinition.templateType === FormDefinitionTemplateTypes.PDF && formDefinition.template === 'EXTERNAL' && (
          <>
            <TextField
              fullWidth
              value={formDefinition.mappingWebhook?.url}
              label={t('formDefinitions.formDefinitionModal.url')}
              onChange={(e: any) => setFormDefinition({ ...formDefinition, mappingWebhook: { ...formDefinition.mappingWebhook, url: e.target.value } })}
              testId='mapping-webhook'
            />
            <TextField
              fullWidth
              type='password'
              value={formDefinition.mappingWebhook?.apiToken}
              label={t('formDefinitions.formDefinitionModal.apiToken')}
              onChange={(e: any) => setFormDefinition({ ...formDefinition, mappingWebhook: { ...formDefinition.mappingWebhook, apiToken: e.target.value } })}
              testId='mapping-apiToken'
            />
          </>
        )}
        {formDefinition.signingType === FormDefinitionSigningTypes.INTEGRATION && (
          <Box display='flex' gap={1} flexDirection='column'>
            <Typography variant='labelLarge'>{t('formDefinitions.signers')}</Typography>
            {formDefinition.signingType === FormDefinitionSigningTypes.INTEGRATION && formDefinition.signerMapping.map((s: SignerMapping, i: number) => (
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <SignerTypeSelect
                    label={t('formDefinitions.formDefinitionModal.signerKey')}
                    selectedSignerType={s.signerKey}
                    onSignerTypeSelect={(e: any) => {
                      const signerMapping = [...formDefinition.signerMapping];
                      signerMapping[i].signerKey = e;
                      setFormDefinition({ ...formDefinition, signerMapping });
                    }}
                    testId={`signer-name-${i}`}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <NumberField label={t('formDefinitions.formDefinitionModal.pageNumber')} number={(s.pageNumber ?? 0).toString()} setNumber={(e: any) => {
                    const signerMapping = [...formDefinition.signerMapping];
                    signerMapping[i].pageNumber = parseInt(e, 10);
                    setFormDefinition({ ...formDefinition, signerMapping });
                  }} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <NumberField label={t('formDefinitions.formDefinitionModal.x')} number={(s.x ?? 0).toString()} setNumber={(e: any) => {
                    const signerMapping = [...formDefinition.signerMapping];
                    signerMapping[i].x = parseInt(e, 10);
                    setFormDefinition({ ...formDefinition, signerMapping });
                  }} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <NumberField label={t('formDefinitions.formDefinitionModal.y')} number={(s.y ?? 0).toString()} setNumber={(e: any) => {
                    const signerMapping = [...formDefinition.signerMapping];
                    signerMapping[i].y = parseInt(e, 10);
                    setFormDefinition({ ...formDefinition, signerMapping });
                  }} />
                </Grid>
                <Grid item xs={12} md={1} display='flex' justifyContent='end' flexDirection='column'>
                  <IconButton onClick={() => {
                    const signerMapping = [...formDefinition.signerMapping];
                    signerMapping.splice(i, 1);
                    setFormDefinition({ ...formDefinition, signerMapping });
                  }}>
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Box display='flex' justifyContent='end'>
              <Button
                variant='tonal'
                leadingIcon={Add}
                size='sm'
                onClick={() => setFormDefinition({
                  ...formDefinition,
                  signerMapping: [
                    ...formDefinition.signerMapping,
                    {
                      signerKey: '', x: 0, y: 0, pageNumber: 0,
                    },
                  ],
                })}
                label={t('shared:add')}
              />
            </Box>
          </Box>
        )}
      </Box>
    </FormModal>
  );
};
